import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Formik, Form, useFormikContext } from "formik";
import { Autocomplete, Box, TextField, SxProps, Theme } from "@mui/material";
import axiosInstant from "../../../api";
import {
    salesPrepPacksEndpoint,
    getProductCategories,
} from "../../../api/endpoints";
import { selectorAutocompleteStyle } from "../../videoProduction/components/selectorStyles";
import { CustomFieldText } from "../../../components/fields/CustomFieldText/CustomFieldText";
import { CustomTextArea } from "../../../components/fields/CustomTextArea/CustomTextArea";
import { CustomFieldLabel } from "../../../components/fields/CustomFieldLabel/CustomFieldLabel";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import { isValidUrl, normalizeUrl } from "../../../components/PdpAudit/schema";
import { useRmnBrand } from "../../../layouts/RmnBrand/RmnBrandContext";
import { PrepPack, ProductCategory, CreatePrepPackPayload } from "./types";
import { IconSparkles, IconChevron, IconClose, IconConfirm } from "../../../utils/icons";
import {
    INITIAL_PREP_PACK_VALUES,
    LEAD_SOURCES,
    MEETING_TYPES,
    MEETING_GOALS,
    WIZARD_STEPS as STEPS,
    PrepPackFormValues,
    CategoryOption,
    buildCategoryOptions,
    catLabel,
    composeCategory,
    summariseSubs,
} from "./constants";

const toOptions = (arr: string[]) => arr.map((v) => ({ label: v, value: v }));

const categorySelectorStyle: SxProps<Theme> = {
    ...(selectorAutocompleteStyle as Record<string, any>),
    height: "auto",
    minHeight: "50px",
    "& .MuiOutlinedInput-root": {
        ...((selectorAutocompleteStyle as any)["& .MuiOutlinedInput-root"] || {}),
        height: "auto",
        minHeight: "50px",
        flexWrap: "wrap",
        alignItems: "center",
        rowGap: "6px",
        paddingTop: "7px",
        paddingBottom: "7px",
    },
};

const MAX_URLS = 5;

// Generation runs in a background Kue job (per-URL PDP audits + the
// multi-stage research/fact-check/assemble/QA pipeline, ~10-20 minutes) —
// the create POST returns immediately and this modal closes right away,
// exactly like the RMN category/brand/comparison report forms. The list's
// Generating row tracks live pipeline stages and flips to Ready on its own
// polling.

const CREATE_ERROR_MESSAGES: Record<string, string> = {
    BrandNameRequired: "Enter the prospect's brand name.",
    ProductUrlsRequired: "Add at least one product page URL.",
    TooManyProductUrls: `Add at most ${MAX_URLS} product page URLs.`,
    InvalidProductUrl: "One of the product URLs isn't a valid http(s) URL.",
};

interface Props {
    onClose: () => void;
    onCreated: (pack: PrepPack) => void;
    // POST target for the new prep pack (defaults to the admin endpoint).
    // The RMN portal passes /rmn/prep-packs.
    createEndpoint?: string;
}

function RevRow({ k, v }: { k: string; v?: string | null }) {
    return (
        <div className="flex items-start justify-between gap-3 py-[5px]">
            <span className="text-[12.5px] text-text-2 shrink-0">{k}</span>
            <span
                className={`text-[13px] text-right break-all ${
                    v ? "font-medium text-text-1" : "text-muted font-normal"
                }`}
            >
                {v || "—"}
            </span>
        </div>
    );
}

const urlInputStyle: React.CSSProperties = {
    flex: 1,
    height: 40,
    padding: "10px 12px",
    borderRadius: 10,
    background: "var(--surface-2)",
    border: "none",
    outline: "none",
    fontFamily: "CircularStd",
    fontSize: 14,
    color: "var(--text)",
};

function PrepPackFormBody({
    onClose,
    onCreated,
    createEndpoint = salesPrepPacksEndpoint,
}: Props) {
    const { values, setFieldValue } = useFormikContext<PrepPackFormValues>();

    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
    const [selectedL1, setSelectedL1] = useState("");
    const [selectedCats, setSelectedCats] = useState<(CategoryOption | string)[]>([]);
    const [touchedUrls, setTouchedUrls] = useState<boolean[]>([false]);

    const l1Options = useMemo(
        () =>
            Array.from(
                new Set(
                    categoryOptions
                        .filter((o) => o.depth === 1 && o.l1 !== "Uncategorized")
                        .map((o) => o.l1),
                ),
            ).sort((a, b) => a.localeCompare(b)),
        [categoryOptions],
    );

    const subOptions = useMemo(
        () =>
            selectedL1
                ? categoryOptions.filter((o) => o.l1 === selectedL1 && o.depth > 1)
                : [],
        [categoryOptions, selectedL1],
    );

    let subPlaceholder = "Narrow it down (optional)";
    if (!selectedL1) subPlaceholder = "Pick a category above first";
    else if (selectedCats.length) subPlaceholder = "";

    const subSummary = summariseSubs(selectedCats);

    // Step one's Brand name flows through to the Detail step's Primary brand.
    // It mirrors (including after back-and-edit) until the rep types their
    // own value; an emptied field re-mirrors on the next brand-name change —
    // the engine falls back to the brand name for an empty primary anyway.
    const autoPrimary = useRef("");
    useEffect(() => {
        const brand = values.brand_name.trim();
        if (!values.primary_brand || values.primary_brand === autoPrimary.current) {
            if (values.primary_brand !== brand) {
                setFieldValue("primary_brand", brand);
            }
            autoPrimary.current = brand;
        }
    }, [values.brand_name]);

    useEffect(() => {
        let active = true;
        // Public route (auth-only) so the RMN portal's brand-side users can
        // load suggestions too — /admin/product-categories 403s for them.
        axiosInstant
            .get(getProductCategories())
            .then(({ data }) => {
                if (!active) return;
                const list = Array.isArray(data) ? data : data?.rows || [];
                setCategoryOptions(buildCategoryOptions(list));
            })
            .catch(() => {
                if (active) setCategoryOptions([]);
            });
        return () => {
            active = false;
        };
    }, []);

    const urls = values.product_urls;
    const setUrl = (idx: number, value: string) =>
        setFieldValue(
            "product_urls",
            urls.map((u, i) => (i === idx ? value : u)),
        );
    const addUrlRow = () => {
        if (urls.length >= MAX_URLS) return;
        setFieldValue("product_urls", [...urls, ""]);
        setTouchedUrls((prev) => [...prev, false]);
    };
    const removeUrlRow = (idx: number) => {
        if (urls.length <= 1) return;
        setFieldValue(
            "product_urls",
            urls.filter((_, i) => i !== idx),
        );
        setTouchedUrls((prev) => prev.filter((_, i) => i !== idx));
    };
    const urlRowError = (idx: number): string | null => {
        if (!touchedUrls[idx]) return null;
        const value = (urls[idx] || "").trim();
        if (!value) return null;
        return isValidUrl(value) ? null : "Enter a valid product page URL";
    };

    const filledUrls = urls.map((u) => (u || "").trim()).filter(Boolean);

    const step1Valid = useMemo(() => {
        if (!values.brand_name.trim()) return false;
        if (filledUrls.length === 0) return false;
        if (!filledUrls.every(isValidUrl)) return false;
        return true;
    }, [values.brand_name, filledUrls]);

    // Step 2 (lead source / meeting type / meeting goal) and the category are
    // optional (TDC-1674): the API accepts them empty and the engine defaults
    // the meeting context, so only step 1 gates progress.
    const blockNext = step === 0 && !step1Valid;

    const handleSubmit = async () => {
        const brandName = values.brand_name.trim();
        if (!brandName || filledUrls.length === 0) return;
        setSubmitting(true);

        try {
            const payload: CreatePrepPackPayload = {
                brand_name: brandName,
                product_urls: [...new Set(filledUrls.map(normalizeUrl))],
                category: values.category.trim(),
                lead_source: values.lead_source || undefined,
                meeting_type: values.meeting_type || undefined,
                meeting_goal: values.meeting_goal || undefined,
                decision_maker: values.decision_maker || undefined,
                notes: values.notes || undefined,
                inputs: {
                    primaryBrand: values.primary_brand,
                    secondaryBrand: values.secondary_brand,
                    keyAttributes: values.key_attributes,
                    adjacentBrands: values.adjacent_brands,
                    subreddits: values.subreddits,
                    pdpFindings: values.pdp_findings,
                },
            };
            const { data } = await axiosInstant.post<PrepPack>(createEndpoint, payload);

            // The pack generates in the background (Kue job) — close the
            // modal immediately, same as the RMN category/brand/comparison
            // reports. The list's Generating row shows live pipeline stages
            // and flips to Ready on its own polling.
            toast.success(
                `Generating the ${brandName} Sales Intel Prep Pack in the background — it takes about 15–30 minutes and flips to Ready in the list when done.`,
            );
            onCreated(data);
        } catch (e: any) {
            const code = e?.response?.data?.error;
            const friendly = typeof code === "string" ? CREATE_ERROR_MESSAGES[code] : undefined;
            toast.error(
                friendly ||
                    (typeof code === "string"
                        ? code
                        : "Couldn't generate the Sales Intel Prep Pack. Please try again."),
            );
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-[rgba(28,26,23,0.4)] z-[1200] flex items-center justify-center p-5 animate-ppFade">
            <div
                className="w-[620px] max-w-full max-h-[calc(100vh_-_40px)] bg-surface rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] flex flex-col overflow-hidden animate-ppPop"
                style={{ fontFamily: "'CircularStd','DM Sans',sans-serif" }}
            >
                <div className="flex items-start justify-between pt-5 px-[22px] pb-[14px]">
                    <div className="flex gap-3 items-center">
                        <div className="w-[38px] h-[38px] rounded-[10px] bg-tint flex items-center justify-center shrink-0">
                            <IconSparkles />
                        </div>
                        <div>
                            <div className="text-[17px] font-bold text-text-1">New Sales Intel Prep Pack</div>
                            <div className="text-[11.5px] text-text-2 mt-px uppercase tracking-[0.4px] font-semibold">
                                Step {step + 1} of {STEPS.length} · {STEPS[step].label}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="w-[30px] h-[30px] rounded-full border border-border-token bg-surface flex items-center justify-center cursor-pointer text-muted shrink-0 hover:border-text-1 hover:text-text-1"
                        onClick={onClose}
                    >
                        <IconClose size={16} />
                    </button>
                </div>

                <div className="flex items-start px-[26px] pb-[38px]">
                    {STEPS.map((s, i) => (
                        <div key={s.key} className="flex items-start flex-1 last:flex-[0]">
                            <div className="relative flex flex-col items-center shrink-0">
                                <div
                                    className={`w-[30px] h-[30px] rounded-full border-[1.5px] flex items-center justify-center text-[10px] font-bold shrink-0 transition-all duration-200 font-['IBM_Plex_Mono',monospace] ${
                                        i < step
                                            ? "bg-accent border-accent text-white"
                                            : i === step
                                            ? "border-accent text-accent-text bg-tint"
                                            : "border-border-token text-muted bg-surface"
                                    }`}
                                >
                                    {i < step ? <IconConfirm size={11} stroke="#FFF" /> : `0${i + 1}`}
                                </div>
                                <span
                                    className={`absolute top-[calc(100%_+_8px)] left-1/2 -translate-x-1/2 text-[10.5px] whitespace-nowrap font-semibold uppercase tracking-[0.4px] transition-colors duration-200 ${
                                        i === step
                                            ? "text-text-1"
                                            : i < step
                                            ? "text-accent-text"
                                            : "text-muted"
                                    }`}
                                >
                                    {s.label}
                                </span>
                            </div>
                            {i < STEPS.length - 1 && (
                                <div
                                    className={`flex-1 h-[1.5px] mt-[15px] mx-[10px] rounded-[1px] ${
                                        i < step ? "bg-accent" : "bg-track"
                                    }`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto pt-1 px-[22px] pb-[14px] min-h-[280px]">
                    {step === 0 && (
                        <>
                            <div className="mb-[18px]">
                                <h3 className="text-[18px] font-extrabold text-text-1 tracking-[-0.2px]">Who is the prospect?</h3>
                                <p className="text-[12.5px] text-text-2 mt-[3px] leading-[1.5]">
                                    Tells the engine who and what to research.
                                </p>
                            </div>

                            <div className="mb-[18px]">
                                <CustomFieldText
                                    name="brand_name"
                                    label="Brand"
                                    placeholder="e.g. AG1"
                                    fullWidth
                                    required
                                />
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Type the prospect's brand name.
                                </p>
                            </div>

                            <div className="mb-[18px]">
                                <CustomFieldLabel required>Product URL(s)</CustomFieldLabel>
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Paste 1–{MAX_URLS} product page URLs. Each page gets a PDP
                                    audit that's woven into the report.
                                </p>
                                <div className="flex flex-col gap-[6px]">
                                    {urls.map((url, idx) => {
                                        const err = urlRowError(idx);
                                        return (
                                            <div key={idx}>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="url"
                                                        placeholder="https://brand.com/products/…"
                                                        aria-label={`Product URL ${idx + 1}`}
                                                        value={url}
                                                        onChange={(e) => setUrl(idx, e.target.value)}
                                                        onBlur={() =>
                                                            setTouchedUrls((prev) =>
                                                                prev.map((t, i) => (i === idx ? true : t)),
                                                            )
                                                        }
                                                        style={{
                                                            ...urlInputStyle,
                                                            border: err ? "1px solid var(--down)" : "none",
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        title="Remove URL"
                                                        aria-label={`Remove URL ${idx + 1}`}
                                                        onClick={() => removeUrlRow(idx)}
                                                        disabled={urls.length <= 1}
                                                        className="w-[30px] h-[30px] rounded-full border border-border-token bg-surface flex items-center justify-center shrink-0 text-muted enabled:cursor-pointer enabled:hover:border-text-1 enabled:hover:text-text-1 disabled:opacity-40"
                                                    >
                                                        <IconClose size={14} />
                                                    </button>
                                                </div>
                                                {err && (
                                                    <div className="text-[12px] text-down mt-[3px]">{err}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {urls.length < MAX_URLS && (
                                    <button
                                        type="button"
                                        onClick={addUrlRow}
                                        className="mt-[9px] bg-transparent border-none p-0 text-[13px] font-semibold text-accent-text cursor-pointer hover:text-text-1"
                                    >
                                        + Add another URL
                                    </button>
                                )}
                            </div>

                            <div className="mb-[18px]">
                                <CustomFieldLabel>Market / Category</CustomFieldLabel>
                                <Box mt={0.5}>
                                    <Autocomplete
                                        freeSolo
                                        forcePopupIcon
                                        options={l1Options}
                                        value={selectedL1}
                                        onChange={(_, v) => {
                                            const next = (v || "").trim();
                                            setSelectedL1(next);
                                            setSelectedCats([]);
                                            setFieldValue("category", composeCategory(next, []));
                                        }}
                                        onInputChange={(_, v, reason) => {
                                            if (reason !== "input") return;
                                            const next = v.trim();
                                            setSelectedL1(next);
                                            setSelectedCats([]);
                                            setFieldValue("category", composeCategory(next, []));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                placeholder="e.g. Beauty and Personal Care, or type any market"
                                                sx={categorySelectorStyle}
                                            />
                                        )}
                                    />
                                </Box>
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Optional. Pick a product category or type any description of the market.
                                    Sharpens the research and expert picks when set.
                                </p>
                            </div>

                            <div className="mb-[18px]">
                                <CustomFieldLabel>Product Sub-Category</CustomFieldLabel>
                                <Box mt={0.5}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        disableCloseOnSelect
                                        forcePopupIcon
                                        disabled={!selectedL1}
                                        options={subOptions}
                                        value={selectedCats}
                                        getOptionLabel={(o) =>
                                            typeof o === "string" ? o : o.subPath || o.name
                                        }
                                        isOptionEqualToValue={(o, v) =>
                                            catLabel(o) === catLabel(v)
                                        }
                                        onChange={(_, v) => {
                                            setSelectedCats(v);
                                            setFieldValue(
                                                "category",
                                                composeCategory(selectedL1, v),
                                            );
                                        }}
                                        renderOption={(props, o) => {
                                            const { key, ...rest } = props as any;
                                            return (
                                                <li
                                                    {...rest}
                                                    key={typeof o === "string" ? o : o.id}
                                                >
                                                    {typeof o === "string" ? o : o.subPath}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                placeholder={subPlaceholder}
                                                sx={categorySelectorStyle}
                                            />
                                        )}
                                    />
                                </Box>
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Optional. Only shows sub-categories inside the category above.
                                </p>
                            </div>
                        </>
                    )}

                    {step === 1 && (
                        <>
                            <div className="mb-[18px]">
                                <h3 className="text-[18px] font-extrabold text-text-1 tracking-[-0.2px]">What's the meeting?</h3>
                                <p className="text-[12.5px] text-text-2 mt-[3px] leading-[1.5]">
                                    Optional. Shapes the meeting game plan and the recommended TDC package.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-[14px] max-[560px]:grid-cols-1">
                                <div className="mb-[18px]">
                                    <CustomFieldLabel>Lead source</CustomFieldLabel>
                                    <Box mt={0.5}>
                                        <CustomSelect
                                            labelId="pp-lead-source"
                                            name="lead_source"
                                            value={values.lead_source}
                                            onChange={(v) => setFieldValue("lead_source", v)}
                                            options={toOptions(LEAD_SOURCES)}
                                            placeholder="Select source…"
                                            width="100%"
                                        />
                                    </Box>
                                </div>
                                <div className="mb-[18px]">
                                    <CustomFieldLabel>Meeting type</CustomFieldLabel>
                                    <Box mt={0.5}>
                                        <CustomSelect
                                            labelId="pp-meeting-type"
                                            name="meeting_type"
                                            value={values.meeting_type}
                                            onChange={(v) => setFieldValue("meeting_type", v)}
                                            options={toOptions(MEETING_TYPES)}
                                            placeholder="Select type…"
                                            width="100%"
                                        />
                                    </Box>
                                </div>
                            </div>
                            <div className="mb-[18px]">
                                <CustomFieldLabel>Meeting goal</CustomFieldLabel>
                                <Box mt={0.5}>
                                    <CustomSelect
                                        labelId="pp-meeting-goal"
                                        name="meeting_goal"
                                        value={values.meeting_goal}
                                        onChange={(v) => setFieldValue("meeting_goal", v)}
                                        options={toOptions(MEETING_GOALS)}
                                        placeholder="Select goal…"
                                        width="100%"
                                    />
                                </Box>
                            </div>
                            <div className="mb-[18px]">
                                <CustomFieldText
                                    name="decision_maker"
                                    label="Decision-maker"
                                    placeholder="e.g. JR Ortiz-Dave, President"
                                    fullWidth
                                />
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Optional. Add the name and title of who you'll be meeting with.
                                </p>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="mb-[18px]">
                                <h3 className="text-[18px] font-extrabold text-text-1 tracking-[-0.2px]">Sharpen the research</h3>
                                <p className="text-[12.5px] text-text-2 mt-[3px] leading-[1.5]">
                                    Skip what you don't know. Each field tightens what the engine
                                    searches for.
                                </p>
                            </div>
                            <div className="flex items-center gap-[7px] py-[9px] px-3 bg-tint rounded-lg text-[12px] text-accent-text mb-4 font-medium">
                                <IconSparkles size={14} stroke="currentColor" /> All optional — but the more
                                you give, the sharper the output.
                            </div>
                            <div className="grid grid-cols-2 gap-[14px] max-[560px]:grid-cols-1">
                                <div className="mb-[18px]">
                                    <CustomFieldText
                                        name="primary_brand"
                                        label="Primary brand"
                                        placeholder="If different from product focus"
                                        fullWidth
                                    />
                                </div>
                                <div className="mb-[18px]">
                                    <CustomFieldText
                                        name="secondary_brand"
                                        label="Secondary brand"
                                        placeholder="If the brand has two lines worth covering"
                                        fullWidth
                                    />
                                </div>
                            </div>
                            <div className="mb-[18px]">
                                <CustomFieldText
                                    name="key_attributes"
                                    label="Key attributes"
                                    placeholder="e.g. probiotics, organic, immune support"
                                    fullWidth
                                />
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Helps voice-of-customer expand when direct mentions are sparse.
                                </p>
                            </div>
                            <div className="mb-[18px]">
                                <CustomFieldText
                                    name="adjacent_brands"
                                    label="Adjacent brands"
                                    placeholder="e.g. AG1, Bloom, Organifi"
                                    fullWidth
                                />
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Competitors and category neighbours the engine should monitor.
                                </p>
                            </div>
                            <div className="mb-[18px]">
                                <CustomFieldText
                                    name="subreddits"
                                    label="Subreddit hints"
                                    placeholder="e.g. r/Supplements, r/nutrition"
                                    fullWidth
                                />
                                <p className="text-[12px] text-text-2 mt-[3px] mb-[9px] leading-[1.4]">
                                    Leave blank to auto-discover the communities and search terms from Google + Reddit.
                                </p>
                            </div>
                            <CustomTextArea
                                name="pdp_findings"
                                label="Additional PDP notes"
                                placeholder="Optional — each product URL is audited automatically; add extra PDP context here…"
                                rows={3}
                            />
                            <CustomTextArea
                                name="notes"
                                label="Additional context & notes"
                                placeholder="Anything else worth knowing…"
                                rows={3}
                            />
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <div className="mb-[18px]">
                                <h3 className="text-[18px] font-extrabold text-text-1 tracking-[-0.2px]">Ready</h3>
                                <p className="text-[12.5px] text-text-2 mt-[3px] leading-[1.5]">
                                    Review the inputs. Each product URL gets a PDP audit and a
                                    Consumer Voice analysis, then the engine generates the brand
                                    intelligence report — all in the background.
                                </p>
                            </div>
                            <div className="py-[14px] px-4 bg-surface-2 border border-border-token rounded-[10px]">
                                <div className="text-[11px] font-bold text-accent-text uppercase tracking-[0.5px] mt-[14px] mb-[6px] pb-1 border-b border-border-token first:mt-0">Prospect</div>
                                <RevRow k="Brand" v={values.brand_name.trim()} />
                                <RevRow k="Market / Category" v={selectedL1 || values.category} />
                                <RevRow k="Product Sub-Category" v={subSummary} />
                                <RevRow
                                    k="Product URLs"
                                    v={filledUrls.length ? filledUrls.join(", ") : null}
                                />
                                <div className="text-[11px] font-bold text-accent-text uppercase tracking-[0.5px] mt-[14px] mb-[6px] pb-1 border-b border-border-token first:mt-0">Context</div>
                                <RevRow k="Lead source" v={values.lead_source} />
                                <RevRow k="Meeting type" v={values.meeting_type} />
                                <RevRow k="Meeting goal" v={values.meeting_goal} />
                                <RevRow k="Decision-maker" v={values.decision_maker} />
                                {(values.primary_brand ||
                                    values.secondary_brand ||
                                    values.key_attributes ||
                                    values.adjacent_brands ||
                                    values.subreddits ||
                                    values.pdp_findings ||
                                    values.notes) && (
                                    <>
                                        <div className="text-[11px] font-bold text-accent-text uppercase tracking-[0.5px] mt-[14px] mb-[6px] pb-1 border-b border-border-token first:mt-0">Detail</div>
                                        <RevRow k="Primary brand" v={values.primary_brand} />
                                        <RevRow k="Secondary brand" v={values.secondary_brand} />
                                        <RevRow k="Key attributes" v={values.key_attributes} />
                                        <RevRow k="Adjacent brands" v={values.adjacent_brands} />
                                        <RevRow k="Subreddit hints" v={values.subreddits} />
                                        <RevRow
                                            k="PDP notes"
                                            v={values.pdp_findings ? "Provided" : null}
                                        />
                                        <RevRow k="Notes" v={values.notes ? "Provided" : null} />
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-between py-4 px-[22px] border-t border-border-token gap-[10px]">
                    {step > 0 ? (
                        <button
                            type="button"
                            className="inline-flex items-center gap-[6px] py-[9px] px-4 rounded-lg text-[13px] font-semibold cursor-pointer transition-all duration-150 border border-border-token bg-surface text-text-2 hover:border-text-1 hover:text-text-1"
                            onClick={() => setStep(step - 1)}
                        >
                            <IconChevron dir="L" /> Back
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="inline-flex items-center gap-[6px] py-[9px] px-4 rounded-lg text-[13px] font-semibold cursor-pointer transition-all duration-150 border border-border-token bg-surface text-text-2 hover:border-text-1 hover:text-text-1"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    )}
                    {step < STEPS.length - 1 ? (
                        <button
                            type="button"
                            className={`inline-flex items-center gap-[6px] py-[9px] px-4 rounded-lg text-[13px] font-semibold transition-all duration-150 border border-transparent ${
                                blockNext
                                    ? "bg-track text-muted cursor-not-allowed"
                                    : "bg-accent text-white cursor-pointer hover:bg-[#B8956A]"
                            }`}
                            disabled={blockNext}
                            onClick={() => setStep(step + 1)}
                        >
                            Next <IconChevron dir="R" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="inline-flex items-center gap-[6px] py-[9px] px-4 rounded-lg text-[13px] font-semibold cursor-pointer transition-all duration-150 border border-transparent bg-accent text-white hover:bg-[#B8956A]"
                            onClick={handleSubmit}
                        >
                            <IconSparkles size={15} stroke="#FFF" /> Generate Prep Pack
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function PrepPackForm(props: Props) {
    // In the RMN sales area the report should also cover the network itself,
    // so the RMN's brand pre-fills Secondary brand (still editable). Outside
    // RmnBrandLayout the context default is brand: null and the internal
    // sales page keeps an empty field.
    const { brand: rmnBrand } = useRmnBrand();
    return (
        <Formik
            initialValues={{
                ...INITIAL_PREP_PACK_VALUES,
                secondary_brand: rmnBrand?.name || "",
            }}
            onSubmit={() => {}}
        >
            <Form>
                <PrepPackFormBody {...props} />
            </Form>
        </Formik>
    );
}
